import { v3Api } from './api';

export const MAIL_TYPES = {
  DECLINE: 'DECLINE',
  APPROVAL: 'APPROVAL',
  SUBMIT: 'SUBMIT',
  VERIFICATION: 'VERIFICATION',
  JOIN: 'JOIN',
  INVITE: 'INVITE',
  PAYMENT_COMPLETED: 'PAYMENT_COMPLETED',
  DEADLINE_APPROACHING: 'DEADLINE_APPROACHING',
  SYNC: 'SYNC',
  ETC: 'ETC',
  // 허용 목록에 없는 타입 → ETC로 처리
  CONFIRM: 'ETC'
};

const CONFIRM_BASE_URL = 'https://report.qesg.co.kr/confirm_user';

/**
 * HTML 특수문자를 이스케이프하여 XSS를 방지
 * 템플릿 리터럴로 HTML에 삽입되는 사용자 입력 값에 반드시 적용할 것
 * @param {string} str - 이스케이프할 문자열
 * @returns {string} 이스케이프된 문자열
 */
function escapeHtml(str) {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * HTML 메일 콘텐츠를 플레인 텍스트로 변환
 * @param {string} html - HTML 문자열
 * @returns {string} 플레인 텍스트
 */
export function htmlToPlainText(html) {
  return html
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<\/h[1-6]>/gi, '\n\n')
    .replace(/<\/tr>/gi, '\n')
    .replace(/<\/td>/gi, '  ')
    .replace(/<\/div>/gi, '\n')
    .replace(/<hr\s*\/?>/gi, '\n---\n')
    .replace(/<a\s+href="([^"]*)"[^>]*>([^<]*)<\/a>/gi, '$2 ($1)')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&copy;/g, '\u00A9')
    .replace(/&rarr;/g, '\u2192')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n /g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * 메일 HTML에 플레인 텍스트 토글 섹션을 추가
 * - 지원 클라이언트(Apple Mail, iOS, Yahoo 등): details/summary 토글로 동작
 * - 미지원 클라이언트(Outlook 등): 펼쳐진 상태로 표시 (graceful degradation)
 * @param {string} mainHtml - 원본 메일 HTML
 * @returns {string}
 */
function wrapWithPlainTextToggle(mainHtml) {
  const plainText = htmlToPlainText(mainHtml);
  const escaped = plainText.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  return `${mainHtml}
<table width="100%" border="0" cellspacing="0" cellpadding="0" style="font-family: 'Pretendard', sans-serif;">
  <tr>
    <td align="center">
      <table border="0" cellspacing="0" cellpadding="0" style="width: 100%; max-width: 600px;">
        <tr>
          <td style="padding: 12px 20px;">
            <details style="width: 100%;">
              <summary style="cursor: pointer; font-size: 13px; color: #636E72; padding: 8px 0;">
                이 메일이 올바르게 표시되지 않나요? 텍스트 버전 보기
              </summary>
              <div style="margin-top: 8px; padding: 16px; background-color: #f8f9fa; border-radius: 8px; border: 1px solid #e9ecef;">
                <pre style="margin: 0; white-space: pre-wrap; word-break: keep-all; font-size: 12px; line-height: 1.6; color: #2d3436; font-family: 'Pretendard', monospace;">${escaped}</pre>
              </div>
            </details>
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>`;
}

/**
 * 공동작성 초대 메일 페이로드 생성
 * @param {string} recipientEmail - 수신자 이메일
 * @param {string} ownerName - 초대자(보고서 소유자) 이름
 * @param {string} reportName - 보고서 이름
 * @returns {{ mail_title, mail_content, mail_type, recipients }}
 */
export function buildInviteMailContent({
  recipientEmail,
  recipientName = '',
  ownerName,
  reportName
}) {
  const safeReportName = escapeHtml(reportName);
  const safeOwnerName = escapeHtml(ownerName);
  const safeRecipientEmail = escapeHtml(recipientEmail);
  const safeRecipientName = escapeHtml(recipientName);
  const mail_title = `[ERT 초대] '${reportName}' 보고서 작성 요청`;
  const recipientDisplay = safeRecipientName
    ? `${safeRecipientName}(${safeRecipientEmail})`
    : safeRecipientEmail;
  const ctaHref = 'https://report.qesg.co.kr/';

  const mail_content = `<table width="100%" border="0" cellspacing="0" cellpadding="0" style="background-color: #f4f7f9; padding: 40px 0; font-family: 'Pretendard', sans-serif;">
  <tr>
    <td align="center">
      <table border="0" cellspacing="0" cellpadding="0" style="width: 100%; max-width: 600px; background-color: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 10px rgba(0,0,0,0.05);">
        <tr>
          <td align="center" style="padding: 40px 20px; background-color: #7A4CD1;">
            <h1 style="margin: 0; color: #ffffff; font-size: 24px; font-weight: 700; letter-spacing: -0.5px;">ESG Reporting Tool</h1>
          </td>
        </tr>
        <tr>
          <td style="padding: 40px 40px 20px 40px;">
            <p style="margin: 0 0 10px 0; font-size: 16px; color: #7A4CD1; font-weight: 600;">초대 알림</p>
            <h2 style="margin: 0 0 20px 0; font-size: 22px; color: #2D3436; line-height: 1.4;">
              <strong>${safeOwnerName}</strong>님이 귀하를<br>
              <span style="color: #00A3A0;">${safeReportName}</span> 작성에 초대했습니다.
            </h2>
            <p style="margin: 0 0 30px 0; font-size: 15px; color: #636E72; line-height: 1.6; word-break: keep-all;">
              안녕하세요, ${recipientDisplay}님.<br>
              퀀티파이드이에스지(QESG)의 ESG Reporting Tool을 통해 데이터 수집부터 목차 구성, 보고서 작성, 번역, 웹 보고서 발간까지 모든 과정을 하나의 환경에서 협업할 수 있습니다.
            </p>
            <table border="0" cellspacing="0" cellpadding="0" style="margin: 0 auto 30px auto;">
              <tr>
                <td align="center" bgcolor="#00A3A0" style="border-radius: 6px;">
                  <a href="${ctaHref}" target="_blank" style="display: inline-block; padding: 16px 32px; font-size: 16px; color: #ffffff; text-decoration: none; font-weight: 600;" rel="noopener">
                    보고서 작성 시작하기
                  </a>
                </td>
                <td style="width: 12px;"></td>
                <td align="center" style="border-radius: 6px; border: 1.5px solid #7A4CD1;">
                  <a href="https://report.qesg.co.kr/signup" target="_blank" style="display: inline-block; padding: 16px 32px; font-size: 16px; color: #7A4CD1; text-decoration: none; font-weight: 600;" rel="noopener">
                    회원가입 하기
                  </a>
                </td>
              </tr>
            </table>
          </td>
        </tr>
        <tr>
          <td style="padding: 0 40px 40px 40px;">
            <p style="margin: 0 0 14px 0; font-size: 14px; color: #2d3436; font-weight: 600;">도움이 필요하신가요?</p>
            <table width="100%" border="0" cellspacing="0" cellpadding="0">
              <tr>
                <td width="48%" valign="top" style="background-color: #f8f9fa; border-radius: 8px 8px 0 0; padding: 18px 18px 12px 18px;">
                  <p style="margin: 0 0 6px 0; font-size: 13px; color: #2d3436; font-weight: 600;">🎮 기능 미리보기</p>
                  <p style="margin: 0; font-size: 12px; color: #636e72; line-height: 1.6; word-break: keep-all;">클릭 한 번으로 ERT의 주요 화면과 기능 흐름을 직접 체험해 볼 수 있어요. 어떻게 사용하는지 먼저 살펴보고 싶다면 여기서 시작하세요.</p>
                </td>
                <td width="4%"></td>
                <td width="48%" valign="top" style="background-color: #f8f9fa; border-radius: 8px 8px 0 0; padding: 18px 18px 12px 18px;">
                  <p style="margin: 0 0 6px 0; font-size: 13px; color: #2d3436; font-weight: 600;">📖 사용자 가이드</p>
                  <p style="margin: 0; font-size: 12px; color: #636e72; line-height: 1.6; word-break: keep-all;">메뉴 구성부터 데이터 입력, 보고서 출력까지 단계별로 정리된 가이드로 처음에도 막힘 없이 사용할 수 있어요.</p>
                </td>
              </tr>
              <tr>
                <td style="background-color: #f8f9fa; border-radius: 0 0 8px 8px; padding: 0 18px 18px 18px;">
                  <a href="https://app.arcade.software/share/collections/isPmuNmupal7elHcWgHF" target="_blank" rel="noopener" style="display: inline-block; font-size: 12px; color: #7a4cd1; text-decoration: none; font-weight: 600; border: 1.5px solid #7a4cd1; padding: 5px 12px; border-radius: 5px;">직접 체험해보기 →</a>
                </td>
                <td></td>
                <td style="background-color: #f8f9fa; border-radius: 0 0 8px 8px; padding: 0 18px 18px 18px;">
                  <a href="https://quantifiedesg-inc.gitbook.io/esg-reporting-tool/" target="_blank" rel="noopener" style="display: inline-block; font-size: 12px; color: #00a3a0; text-decoration: none; font-weight: 600; border: 1.5px solid #00a3a0; padding: 5px 12px; border-radius: 5px;">가이드 읽어보기 →</a>
                </td>
              </tr>
            </table>
          </td>
        </tr>
        <tr>
          <td style="padding: 20px 40px 40px 40px;">
            <p style="margin: 0; font-size: 13px; color: #B2BEC3; line-height: 1.5;">
              본 메일은 지정된 수신자만 이용 가능합니다. <br/>만약 본인이 아니라면 info@qesg.co.kr로 문의해 주시기 바랍니다.<br><br>
              &copy; 2026 Quantified ESG. All rights reserved.
            </p>
          </td>
        </tr>
      </table>
      <table border="0" cellspacing="0" cellpadding="0" style="width: 100%; max-width: 600px; margin-top: 20px;">
        <tr>
          <td align="center">
            <p style="font-size: 12px; color: #B2BEC3;">
              <a href="https://report.qesg.co.kr/" style="color: #B2BEC3; text-decoration: underline;">ERT 웹사이트 방문</a> |
              <span style="color: #B2BEC3;">문의. info@qesg.co.kr</span>
            </p>
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>`;

  return {
    mail_title,
    mail_content: wrapWithPlainTextToggle(mail_content),
    mail_type: MAIL_TYPES.INVITE,
    recipients: [{ email: recipientEmail, variables: {} }]
  };
}

/**
 * 소유권 이전 요청 메일 페이로드 생성
 * @param {string} recipientEmail - 수신자 이메일
 * @param {string} senderName - 발신자(현 소유자) 이름
 * @param {string} reportName - 보고서 이름
 * @param {string|null} confirmCode - 확인 코드
 * @returns {{ mail_title, mail_content, mail_type, recipients }}
 */
export function buildOwnershipTransferMailContent({
  recipientEmail,
  senderName,
  reportName,
  confirmCode
}) {
  const safeSenderName = escapeHtml(senderName);
  const safeReportName = escapeHtml(reportName);
  const safeRecipientEmail = escapeHtml(recipientEmail);
  const mail_title = `[QESG] ${reportName} 소유권 이전 요청`;

  let ctaHref;
  let ctaNote = '';
  if (confirmCode) {
    ctaHref = `${CONFIRM_BASE_URL}?e_mail=${encodeURIComponent(
      recipientEmail
    )}&code=${confirmCode}&mode=ownership`;
  } else {
    ctaHref = '#';
    ctaNote = `<p style="margin: 8px 0 0 0; font-size: 12px; color: #6c757d; text-align: center;">(발송 시 확인 링크가 자동 생성됩니다)</p>`;
  }

  const mail_content = `<table width="100%" border="0" cellspacing="0" cellpadding="0" style="background-color: #f4f7f9; padding: 40px 0; font-family: 'Pretendard', sans-serif;">
  <tr>
    <td align="center">
      <table border="0" cellspacing="0" cellpadding="0" style="width: 100%; max-width: 600px; background-color: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 10px rgba(0,0,0,0.05);">
        <tr>
          <td align="center" style="padding: 40px 20px; background-color: #7A4CD1;">
            <h1 style="margin: 0; color: #ffffff; font-size: 24px; font-weight: 700; letter-spacing: -0.5px;">ESG Reporting Tool</h1>
          </td>
        </tr>
        <tr>
          <td style="padding: 40px 40px 20px 40px;">
            <p style="margin: 0 0 10px 0; font-size: 16px; color: #7A4CD1; font-weight: 600;">소유권 이전 요청</p>
            <h2 style="margin: 0 0 20px 0; font-size: 22px; color: #2D3436; line-height: 1.4;">
              <strong>${safeSenderName}</strong>님이 귀하에게<br>
              <span style="color: #00A3A0;">${safeReportName}</span>의 소유권을 이전하고자 합니다.
            </h2>
            <p style="margin: 0 0 30px 0; font-size: 15px; color: #636E72; line-height: 1.6; word-break: keep-all;">
              안녕하세요, ${safeRecipientEmail}님.<br>
              아래 버튼을 클릭하여 소유권 이전을 수락하시면 해당 보고서의 소유자가 됩니다.
            </p>
            <table border="0" cellspacing="0" cellpadding="0" style="margin: 0 auto 30px auto;">
              <tr>
                <td align="center" bgcolor="#00A3A0" style="border-radius: 6px;">
                  <a href="${ctaHref}" target="_blank" style="display: inline-block; padding: 16px 40px; font-size: 16px; color: #ffffff; text-decoration: none; font-weight: 600;" rel="noopener">
                    소유권 이전 수락하기
                  </a>
                </td>
              </tr>
            </table>
            ${ctaNote}
            <div style="padding: 20px; background-color: #F8F9FA; border-radius: 8px;">
              <p style="margin: 0 0 6px 0; font-size: 13px; color: #636E72; line-height: 1.6;">
                본 요청을 모르신다면 이 이메일을 무시해 주세요.<br>
                문의 사항이 있으시면 <a href="mailto:info@qesg.co.kr" style="color: #7A4CD1; text-decoration: none; font-weight: 600;">info@qesg.co.kr</a>로 연락해 주세요.
              </p>
            </div>
          </td>
        </tr>
        <tr>
          <td style="padding: 20px 40px 40px 40px;">
            <p style="margin: 0; font-size: 13px; color: #B2BEC3; line-height: 1.5;">
              본 메일은 지정된 수신자만 이용 가능합니다. <br/>만약 본인이 아니라면 info@qesg.co.kr로 문의해 주시기 바랍니다.<br><br>
              &copy; 2026 Quantified ESG. All rights reserved.
            </p>
          </td>
        </tr>
      </table>
      <table border="0" cellspacing="0" cellpadding="0" style="width: 100%; max-width: 600px; margin-top: 20px;">
        <tr>
          <td align="center">
            <p style="font-size: 12px; color: #B2BEC3;">
              <a href="https://report.qesg.co.kr/" style="color: #B2BEC3; text-decoration: underline;">ERT 웹사이트 방문</a> |
              <span style="color: #B2BEC3;">문의. info@qesg.co.kr</span>
            </p>
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>`;

  return {
    mail_title,
    mail_content: wrapWithPlainTextToggle(mail_content),
    mail_type: MAIL_TYPES.ETC,
    recipients: [{ email: recipientEmail, variables: {} }]
  };
}

/**
 * 회원가입 완료 환영 메일 페이로드 생성
 * @param {string} recipientEmail - 수신자 이메일
 * @param {string} userName - 가입자 이름
 * @returns {{ mail_title, mail_content, mail_type, recipients }}
 */
export function buildSignupMailContent({ recipientEmail, userName }) {
  const safeUserName = escapeHtml(userName);
  const mail_title = '[QESG] 가입을 환영합니다!';

  const mail_content = `<table width="100%" border="0" cellspacing="0" cellpadding="0" style="background-color: #f4f7f9; padding: 40px 0; font-family: 'Pretendard', sans-serif;">
  <tr>
    <td align="center">
      <table border="0" cellspacing="0" cellpadding="0" style="width: 100%; max-width: 600px; background-color: #ffffff; border-radius: 16px; overflow: hidden; box-shadow: 0 10px 30px rgba(0,0,0,0.05);">
        <tr>
          <td align="center" style="padding: 40px 20px; background-color: #7a4cd1;">
            <h1 style="margin: 0; color: #ffffff; font-size: 24px; font-weight: bold; letter-spacing: -0.5px;">ESG Reporting Tool</h1>
          </td>
        </tr>
        <tr>
          <td style="padding: 40px 40px 20px 40px; text-align: center;">
            <h2 style="margin: 0 0 15px 0; font-size: 22px; color: #2d3436;">가입을 진심으로 환영합니다!</h2>
            <p style="margin: 0 0 30px 0; font-size: 16px; color: #636e72; line-height: 1.6; word-break: keep-all;">
              <strong>${safeUserName}</strong>님, 이제 퀀티파이드이에스지(QESG)의 ERT와 함께 전문적인 ESG 리포팅을 시작해 보세요.
            </p>
            <table border="0" cellspacing="0" cellpadding="0" style="margin: 0 auto 40px auto;">
              <tr>
                <td align="center" bgcolor="#00A3A0" style="border-radius: 8px;">
                  <a href="https://report.qesg.co.kr/" target="_blank" style="display: inline-block; padding: 18px 50px; font-size: 16px; color: #ffffff; text-decoration: none; font-weight: bold;" rel="noopener">
                    ERT 홈페이지로 이동하기
                  </a>
                </td>
              </tr>
            </table>
          </td>
        </tr>
        <!-- Step 섹션 -->
        <tr>
          <td style="padding: 0 40px 40px 40px;">
            <div style="background-color: #f8f9fa; border-radius: 12px; padding: 30px;">
              <h3 style="margin: 0 0 24px 0; font-size: 17px; color: #2d3436;">ERT 시작하기 3단계</h3>
              <!-- Step 1: 회원가입 (완료) -->
              <table width="100%" border="0" cellspacing="0" cellpadding="0">
                <tr>
                  <td width="40" valign="top">
                    <span style="display: inline-block; width: 24px; height: 24px; background-color: #adb5bd; color: #ffffff; border-radius: 50%; text-align: center; font-size: 14px; font-weight: bold; line-height: 24px;">✓</span>
                  </td>
                  <td style="font-size: 15px; color: #adb5bd; line-height: 1.5;">
                    <strong style="color: #adb5bd;">회원가입</strong><br />
                    <span style="font-size: 13px;">회원가입을 완료하셨습니다.</span>
                  </td>
                </tr>
              </table>
              <!-- Step 구분선 -->
              <table width="100%" border="0" cellspacing="0" cellpadding="0">
                <tr>
                  <td width="40" align="center" style="padding: 4px 0;">
                    <span style="display: inline-block; width: 1px; height: 20px; background-color: #dee2e6;"></span>
                  </td>
                  <td></td>
                </tr>
              </table>
              <!-- Step 2: 보고서 세팅 (선택) -->
              <table width="100%" border="0" cellspacing="0" cellpadding="0">
                <tr>
                  <td width="40" valign="top">
                    <span style="display: inline-block; width: 24px; height: 24px; background-color: #7a4cd1; color: #ffffff; border-radius: 50%; text-align: center; font-size: 14px; font-weight: bold; line-height: 24px;">2</span>
                  </td>
                  <td style="font-size: 15px; color: #2d3436; line-height: 1.5;">
                    <strong>보고서 설정하기</strong><br />
                    <span style="font-size: 13px; color: #636e72;">기업 정보를 등록하고, 업종에 맞는 표준 목차를 불러와 보고서 구성을 완성하세요.</span>
                    <table width="100%" border="0" cellspacing="0" cellpadding="0" style="margin-top: 10px;">
                      <tr>
                        <td style="background-color: #f0ebfc; border-radius: 6px; padding: 10px 14px;">
                          <p style="margin: 0; font-size: 12px; color: #7a4cd1; line-height: 1.6;">이미 초대받은 보고서가 있다면 이 단계를 건너뛰고 바로 3단계로 이동하세요.</p>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>
              <!-- Step 구분선 -->
              <table width="100%" border="0" cellspacing="0" cellpadding="0">
                <tr>
                  <td width="40" align="center" style="padding: 4px 0;">
                    <span style="display: inline-block; width: 1px; height: 20px; background-color: #dee2e6;"></span>
                  </td>
                  <td></td>
                </tr>
              </table>
              <!-- Step 3: 보고서 작성하기 -->
              <table width="100%" border="0" cellspacing="0" cellpadding="0">
                <tr>
                  <td width="40" valign="top">
                    <span style="display: inline-block; width: 24px; height: 24px; background-color: #00a3a0; color: #ffffff; border-radius: 50%; text-align: center; font-size: 14px; font-weight: bold; line-height: 24px;">3</span>
                  </td>
                  <td style="font-size: 15px; color: #2d3436; line-height: 1.5;">
                    <strong>보고서 작성하기</strong><br />
                    <span style="font-size: 13px; color: #636e72;">목차 구성부터 데이터 수집, 번역과 발간까지 통합된 협업 환경에서 보고서를 빠르게 완성하세요.</span>
                  </td>
                </tr>
              </table>
            </div>
          </td>
        </tr>
        <!-- 도움이 필요하신가요? 좌우 2단 섹션 -->
        <tr>
          <td style="padding: 0 40px 40px 40px;">
            <p style="margin: 0 0 14px 0; font-size: 14px; color: #2d3436; font-weight: 600;">도움이 필요하신가요?</p>
            <table width="100%" border="0" cellspacing="0" cellpadding="0">
              <tr>
                <td width="48%" valign="top" style="background-color: #f8f9fa; border-radius: 8px 8px 0 0; padding: 18px 18px 12px 18px;">
                  <p style="margin: 0 0 6px 0; font-size: 13px; color: #2d3436; font-weight: 600;">🎮 기능 미리보기</p>
                  <p style="margin: 0; font-size: 12px; color: #636e72; line-height: 1.6; word-break: keep-all;">클릭 한 번으로 ERT의 주요 화면과 기능 흐름을 직접 체험해 볼 수 있어요. 어떻게 사용하는지 먼저 살펴보고 싶다면 여기서 시작하세요.</p>
                </td>
                <td width="4%"></td>
                <td width="48%" valign="top" style="background-color: #f8f9fa; border-radius: 8px 8px 0 0; padding: 18px 18px 12px 18px;">
                  <p style="margin: 0 0 6px 0; font-size: 13px; color: #2d3436; font-weight: 600;">📖 사용자 가이드</p>
                  <p style="margin: 0; font-size: 12px; color: #636e72; line-height: 1.6; word-break: keep-all;">메뉴 구성부터 데이터 입력, 보고서 출력까지 단계별로 정리된 가이드로 처음에도 막힘 없이 사용할 수 있어요.</p>
                </td>
              </tr>
              <tr>
                <td style="background-color: #f8f9fa; border-radius: 0 0 8px 8px; padding: 0 18px 18px 18px;">
                  <a href="https://app.arcade.software/share/collections/isPmuNmupal7elHcWgHF" target="_blank" rel="noopener" style="display: inline-block; font-size: 12px; color: #7a4cd1; text-decoration: none; font-weight: 600; border: 1.5px solid #7a4cd1; padding: 5px 12px; border-radius: 5px;">직접 체험해보기 →</a>
                </td>
                <td></td>
                <td style="background-color: #f8f9fa; border-radius: 0 0 8px 8px; padding: 0 18px 18px 18px;">
                  <a href="https://quantifiedesg-inc.gitbook.io/esg-reporting-tool/" target="_blank" rel="noopener" style="display: inline-block; font-size: 12px; color: #00a3a0; text-decoration: none; font-weight: 600; border: 1.5px solid #00a3a0; padding: 5px 12px; border-radius: 5px;">가이드 읽어보기 →</a>
                </td>
              </tr>
            </table>
          </td>
        </tr>
        <!-- 푸터 -->
        <tr>
          <td style="padding: 0 40px 40px 40px;">
            <p style="margin: 0; font-size: 13px; color: #b2bec3; line-height: 1.5;">
              본 메일은 지정된 수신자만 이용 가능합니다. <br/>만약 본인이 아니라면 info@qesg.co.kr로 문의해 주시기 바랍니다.<br /><br />
              &copy; 2026 Quantified ESG. All rights reserved.
            </p>
          </td>
        </tr>
      </table>
      <table border="0" cellspacing="0" cellpadding="0" style="width: 100%; max-width: 600px; margin-top: 20px;">
        <tr>
          <td align="center">
            <p style="font-size: 12px; color: #b2bec3;">
              <a href="https://report.qesg.co.kr/" style="color: #b2bec3; text-decoration: underline;">ERT 웹사이트 방문</a> |
              <span style="color: #b2bec3;">문의. info@qesg.co.kr</span>
            </p>
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>`;

  return {
    mail_title,
    mail_content: wrapWithPlainTextToggle(mail_content),
    mail_type: MAIL_TYPES.JOIN,
    recipients: [{ email: recipientEmail, variables: { name: userName } }]
  };
}

/**
 * 회원가입 이메일 인증 코드 메일 페이로드 생성
 * @param {string} recipientEmail - 수신자 이메일
 * @param {string} confirmCode - 인증 코드
 * @returns {{ mail_title, mail_content, mail_type, recipients }}
 */
export function buildConfirmMailContent({ recipientEmail, confirmCode }) {
  const safeConfirmCode = escapeHtml(confirmCode);
  const mail_title = '[ERT] 이메일 인증번호 안내';

  const confirmUrl = `${CONFIRM_BASE_URL}?e_mail=${encodeURIComponent(
    recipientEmail
  )}&code=${encodeURIComponent(confirmCode)}&mode=signup`;
  const mail_content = `<table width="100%" border="0" cellspacing="0" cellpadding="0" style="background-color: #f4f7f9; padding: 60px 0; font-family: 'Pretendard', sans-serif;">
        <tr>
            <td align="center">
                <table border="0" cellspacing="0" cellpadding="0" style="width: 100%; max-width: 600px; background-color: #ffffff; border-radius: 16px; overflow: hidden; box-shadow: 0 10px 30px rgba(0,0,0,0.08);">
                    <tr>
                        <td align="center" style="padding: 30px 20px; background-color: #7A4CD1;">
                            <h1 style="margin: 0; color: #ffffff; font-size: 24px; font-weight: 700; letter-spacing: -0.5px;">ESG Reporting Tool</h1>
                        </td>
                    </tr>
                    <tr>
                        <td style="padding: 50px 40px; text-align: center;">
                            <h1 style="margin: 0 0 20px 0; font-size: 24px; color: #2D3436; font-weight: 700;">이메일 주소를 인증해 주세요</h1>
                            <p style="margin: 0 0 30px 0; font-size: 15px; color: #636E72; line-height: 1.6; word-break: keep-all;">
                                <strong>ERT(ESG Reporting Tool)</strong> 서비스 가입을 환영합니다!<br>
                                아래의 인증 코드를 복사하여 가입 화면에 입력하거나, 버튼을 클릭하여 인증을 완료해 주세요.
                            </p>
                            <div style="margin-bottom: 30px; padding: 20px; background-color: #F8F9FA; border-radius: 12px; border: 1px dashed #D1D8E0;">
                                <span style="display: block; font-size: 12px; color: #B2BEC3; margin-bottom: 8px; font-weight: 600; text-transform: uppercase;">Verification Code</span>
                                <span style="font-size: 32px; color: #7A4CD1; font-weight: 800; letter-spacing: 5px;">${safeConfirmCode}</span>
                            </div>
                            <table border="0" cellspacing="0" cellpadding="0" style="margin: 0 auto 40px auto;">
                                <tr>
                                    <td align="center" bgcolor="#00A3A0" style="border-radius: 8px;">
                                        <a href="${confirmUrl}" target="_blank" style="display: inline-block; padding: 18px 50px; font-size: 16px; color: #ffffff; text-decoration: none; font-weight: 700;">
                                            이메일 인증 완료하기
                                        </a>
                                    </td>
                                </tr>
                            </table>
                        </td>
                    </tr>
                    <tr>
          <td style="padding: 0 40px 40px 40px;">
            <p style="margin: 0 0 14px 0; font-size: 14px; color: #2d3436; font-weight: 600;">도움이 필요하신가요?</p>
            <table width="100%" border="0" cellspacing="0" cellpadding="0">
              <tr>
                <td width="48%" valign="top" style="background-color: #f8f9fa; border-radius: 8px 8px 0 0; padding: 18px 18px 12px 18px;">
                  <p style="margin: 0 0 6px 0; font-size: 13px; color: #2d3436; font-weight: 600;">🎮 기능 미리보기</p>
                  <p style="margin: 0; font-size: 12px; color: #636e72; line-height: 1.6; word-break: keep-all;">클릭 한 번으로 ERT의 주요 화면과 기능 흐름을 직접 체험해 볼 수 있어요. 어떻게 사용하는지 먼저 살펴보고 싶다면 여기서 시작하세요.</p>
                </td>
                <td width="4%"></td>
                <td width="48%" valign="top" style="background-color: #f8f9fa; border-radius: 8px 8px 0 0; padding: 18px 18px 12px 18px;">
                  <p style="margin: 0 0 6px 0; font-size: 13px; color: #2d3436; font-weight: 600;">📖 사용자 가이드</p>
                  <p style="margin: 0; font-size: 12px; color: #636e72; line-height: 1.6; word-break: keep-all;">메뉴 구성부터 데이터 입력, 보고서 출력까지 단계별로 정리된 가이드로 처음에도 막힘 없이 사용할 수 있어요.</p>
                </td>
              </tr>
              <tr>
                <td style="background-color: #f8f9fa; border-radius: 0 0 8px 8px; padding: 0 18px 18px 18px;">
                  <a href="https://app.arcade.software/share/collections/isPmuNmupal7elHcWgHF" target="_blank" rel="noopener" style="display: inline-block; font-size: 12px; color: #7a4cd1; text-decoration: none; font-weight: 600; border: 1.5px solid #7a4cd1; padding: 5px 12px; border-radius: 5px;">직접 체험해보기 →</a>
                </td>
                <td></td>
                <td style="background-color: #f8f9fa; border-radius: 0 0 8px 8px; padding: 0 18px 18px 18px;">
                  <a href="https://quantifiedesg-inc.gitbook.io/esg-reporting-tool/" target="_blank" rel="noopener" style="display: inline-block; font-size: 12px; color: #00a3a0; text-decoration: none; font-weight: 600; border: 1.5px solid #00a3a0; padding: 5px 12px; border-radius: 5px;">가이드 읽어보기 →</a>
                </td>
              </tr>
            </table>
          </td>
        </tr>
                    <tr>
                        <td style="padding: 0 40px 40px 40px; text-align: center;">
                            <div style="border-top: 1px solid #F1F2F6; padding-top: 20px;">
                                <p style="margin: 0; font-size: 12px; color: #B2BEC3; line-height: 1.5;">
                                    본 메일은 지정된 수신자만 이용 가능합니다. <br />만약 본인이 아니라면 info@qesg.co.kr로 문의해 주시기 바랍니다.<br><br>
                                    &copy; 2026 Quantified ESG. All rights reserved.
                                </p>
                            </div>
                        </td>
                    </tr>
                </table>
                <table border="0" cellspacing="0" cellpadding="0" style="width: 100%; max-width: 600px; margin-top: 20px;">
                    <tr>
                        <td align="center">
                            <p style="font-size: 12px; color: #B2BEC3;">
                                <a href="https://report.qesg.co.kr/" style="color: #B2BEC3; text-decoration: underline;">ERT 웹사이트 방문</a> |
                                <span style="color: #B2BEC3;">문의. info@qesg.co.kr</span>
                            </p>
                        </td>
                    </tr>
                </table>
            </td>
        </tr>
    </table>`;

  return {
    mail_title,
    mail_content: wrapWithPlainTextToggle(mail_content),
    mail_type: MAIL_TYPES.CONFIRM,
    recipients: [{ email: recipientEmail, variables: {} }]
  };
}

/**
 * 결제 완료 메일 페이로드 생성 (미리보기 전용 — 실제 발송은 백엔드에서 직접 처리)
 * @param {string} recipientEmail - 수신자 이메일
 * @param {string} userName - 수신자 이름 (ESG 담당자)
 * @param {string} planName - 구독 플랜명
 * @param {string} paymentTime - 결제 일시
 * @param {string} paymentMethod - 결제 수단
 * @param {string} paymentAmount - 결제 금액
 * @returns {{ mail_title, mail_content, mail_type: 'PAYMENT_COMPLETED', recipients }}
 */
export function buildPaymentMailContent({
  recipientEmail,
  userName,
  planName,
  paymentTime,
  paymentMethod,
  paymentAmount
}) {
  const safeUserName = escapeHtml(userName);
  const safeRecipientEmail = escapeHtml(recipientEmail);
  const safePlanName = escapeHtml(planName);
  const safePaymentTime = escapeHtml(paymentTime);
  const safePaymentMethod = escapeHtml(paymentMethod);
  const safePaymentAmount = escapeHtml(paymentAmount);
  const mail_title = `[QESG] ${planName} 결제가 완료되었습니다`;

  const mail_content = `<table width="100%" border="0" cellspacing="0" cellpadding="0" style="background-color: #f4f7f9; padding: 60px 0; font-family: 'Pretendard', sans-serif;">
  <tr>
    <td align="center">
      <table border="0" cellspacing="0" cellpadding="0" style="width: 100%; max-width: 600px; background-color: #ffffff; border-radius: 16px; overflow: hidden; box-shadow: 0 10px 30px rgba(0,0,0,0.08);">
        <tr>
          <td align="center" style="padding: 30px 20px; background-color: #7A4CD1;">
            <h1 style="margin: 0; color: #ffffff; font-size: 24px; font-weight: 700; letter-spacing: -0.5px;">ESG Reporting Tool</h1>
          </td>
        </tr>
        <tr>
          <td style="padding: 40px 40px 20px 40px; text-align: center;">
            <div style="display: inline-block; background-color: #F0EBFC; border-radius: 50%; width: 60px; height: 60px; line-height: 60px; text-align: center; font-size: 28px; margin-bottom: 16px;">✓</div>
            <h1 style="margin: 0 0 10px 0; font-size: 22px; color: #2D3436; font-weight: 700;">결제가 완료되었습니다</h1>
            <p style="margin: 0; font-size: 15px; color: #636E72; line-height: 1.6; word-break: keep-all;">
              <strong>${safeUserName}</strong>님, <strong>${safePlanName}</strong> 구독 결제가 성공적으로 처리되었습니다.
            </p>
          </td>
        </tr>
        <tr>
          <td style="padding: 20px 40px 30px 40px;">
            <table width="100%" border="0" cellspacing="0" cellpadding="0" style="background-color: #F8F9FA; border-radius: 12px; overflow: hidden;">
              <tr>
                <td style="padding: 16px 20px; border-bottom: 1px solid #E9ECEF;">
                  <p style="margin: 0; font-size: 12px; color: #B2BEC3; text-transform: uppercase; font-weight: 700; letter-spacing: 0.5px;">결제 영수증</p>
                </td>
              </tr>
              <tr>
                <td style="padding: 14px 20px; border-bottom: 1px solid #E9ECEF;">
                  <table width="100%" border="0" cellspacing="0" cellpadding="0">
                    <tr>
                      <td style="font-size: 13px; color: #636E72;">수신자</td>
                      <td align="right" style="font-size: 13px; color: #2D3436; font-weight: 600;">${safeUserName} (${safeRecipientEmail})</td>
                    </tr>
                  </table>
                </td>
              </tr>
              <tr>
                <td style="padding: 14px 20px; border-bottom: 1px solid #E9ECEF;">
                  <table width="100%" border="0" cellspacing="0" cellpadding="0">
                    <tr>
                      <td style="font-size: 13px; color: #636E72;">플랜</td>
                      <td align="right" style="font-size: 13px; color: #2D3436; font-weight: 600;">${safePlanName}</td>
                    </tr>
                  </table>
                </td>
              </tr>
              <tr>
                <td style="padding: 14px 20px; border-bottom: 1px solid #E9ECEF;">
                  <table width="100%" border="0" cellspacing="0" cellpadding="0">
                    <tr>
                      <td style="font-size: 13px; color: #636E72;">결제 일시</td>
                      <td align="right" style="font-size: 13px; color: #2D3436; font-weight: 600;">${safePaymentTime}</td>
                    </tr>
                  </table>
                </td>
              </tr>
              <tr>
                <td style="padding: 14px 20px; border-bottom: 1px solid #E9ECEF;">
                  <table width="100%" border="0" cellspacing="0" cellpadding="0">
                    <tr>
                      <td style="font-size: 13px; color: #636E72;">결제 수단</td>
                      <td align="right" style="font-size: 13px; color: #2D3436; font-weight: 600;">${safePaymentMethod}</td>
                    </tr>
                  </table>
                </td>
              </tr>
              <tr>
                <td style="padding: 16px 20px; background-color: #F0EBFC;">
                  <table width="100%" border="0" cellspacing="0" cellpadding="0">
                    <tr>
                      <td style="font-size: 14px; color: #7A4CD1; font-weight: 700;">결제 금액</td>
                      <td align="right" style="font-size: 18px; color: #7A4CD1; font-weight: 800;">${safePaymentAmount}</td>
                    </tr>
                  </table>
                </td>
              </tr>
            </table>
          </td>
        </tr>
        <tr>
          <td style="padding: 0 40px 30px 40px; text-align: center;">
            <table border="0" cellspacing="0" cellpadding="0" style="margin: 0 auto;">
              <tr>
                <td align="center" bgcolor="#00A3A0" style="border-radius: 8px;">
                  <a href="https://report.qesg.co.kr/" target="_blank" style="display: inline-block; padding: 14px 40px; font-size: 15px; color: #ffffff; text-decoration: none; font-weight: 700;" rel="noopener">
                    ERT 홈페이지로 이동하기
                  </a>
                </td>
              </tr>
            </table>
          </td>
        </tr>
        <tr>
          <td style="padding: 0 40px 40px 40px; text-align: center; border-top: 1px solid #F1F2F6;">
            <p style="margin: 20px 0 0 0; font-size: 12px; color: #B2BEC3; line-height: 1.6;">
              본 메일은 지정된 수신자만 이용 가능합니다. <br />만약 본인이 아니라면 info@qesg.co.kr로 문의해 주시기 바랍니다.<br><br>
              &copy; 2026 Quantified ESG. All rights reserved.
            </p>
          </td>
        </tr>
      </table>
      <table border="0" cellspacing="0" cellpadding="0" style="width: 100%; max-width: 600px; margin-top: 20px;">
        <tr>
          <td align="center">
            <p style="font-size: 12px; color: #B2BEC3;">
              <a href="https://report.qesg.co.kr/" style="color: #B2BEC3; text-decoration: underline;">ERT 웹사이트 방문</a> |
              <span style="color: #B2BEC3;">문의. info@qesg.co.kr</span>
            </p>
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>`;

  return {
    mail_title,
    mail_content: wrapWithPlainTextToggle(mail_content),
    mail_type: MAIL_TYPES.PAYMENT_COMPLETED,
    recipients: [{ email: recipientEmail, variables: { name: userName } }]
  };
}

/**
 * 결제 만료 알림 메일 페이로드 생성 (미리보기 전용 — 실제 발송은 백엔드에서 직접 처리)
 * @param {string} recipientEmail - 수신자 이메일
 * @param {string} userName - 수신자 이름 (ESG 담당자)
 * @param {string} planName - 구독 플랜명
 * @param {string} expiryDate - 만료 예정일
 * @param {string} paymentAmount - 갱신 금액
 * @returns {{ mail_title, mail_content, mail_type: 'DEADLINE_APPROACHING', recipients }}
 */
export function buildPaymentExpiryMailContent({
  recipientEmail,
  userName,
  planName,
  expiryDate,
  paymentAmount
}) {
  const safeUserName = escapeHtml(userName);
  const safeRecipientEmail = escapeHtml(recipientEmail);
  const safePlanName = escapeHtml(planName);
  const safeExpiryDate = escapeHtml(expiryDate);
  const safePaymentAmount = escapeHtml(paymentAmount);
  const mail_title = `[QESG] ${planName} 구독이 곧 만료됩니다`;

  const mail_content = `<table width="100%" border="0" cellspacing="0" cellpadding="0" style="background-color: #f4f7f9; padding: 40px 0; font-family: 'Pretendard', sans-serif;">
  <tr>
    <td align="center">
      <table border="0" cellspacing="0" cellpadding="0" style="width: 100%; max-width: 600px; background-color: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 10px rgba(0,0,0,0.05);">
        <!-- 헤더 -->
        <tr>
          <td align="center" style="padding: 40px 20px; background-color: #7a4cd1;">
            <h1 style="margin: 0; color: #ffffff; font-size: 24px; font-weight: bold; letter-spacing: -0.5px;">ESG Reporting Tool</h1>
          </td>
        </tr>
        <!-- 타이틀 -->
        <tr>
          <td style="padding: 40px 40px 30px 40px;">
            <p style="margin: 0 0 10px 0; font-size: 16px; color: #e67e22; font-weight: 600;">구독 만료 예정 안내</p>
            <h2 style="margin: 0 0 20px 0; font-size: 22px; color: #2d3436; line-height: 1.4;">
              <strong>${safeUserName}</strong>님이 사용 중이신 <span style="color: #00a3a0;">${safePlanName}</span>이<br />
              곧 만료될 예정입니다.
            </h2>
            <p style="margin: 0; font-size: 15px; color: #636e72; line-height: 1.6; word-break: keep-all;">
              안녕하세요, <strong>${safeUserName}</strong>님 (<a href="mailto:${safeRecipientEmail}" style="color: #7a4cd1; text-decoration: none;">${safeRecipientEmail}</a>).<br />
              현재 이용 중이신 플랜의 만료일이 다가오고 있습니다. 서비스를 계속 이용하시려면 만료일 전에 결제를 진행해 주세요.
            </p>
          </td>
        </tr>
        <!-- 만료 정보 카드 -->
        <tr>
          <td style="padding: 0 40px 30px 40px;">
            <div style="background-color: #fff8f0; border-radius: 12px; padding: 30px;">
              <h3 style="margin: 0 0 20px 0; font-size: 15px; color: #2d3436; font-weight: 600;">플랜 만료 정보</h3>
              <table width="100%" border="0" cellspacing="0" cellpadding="0">
                <!-- 플랜명 -->
                <tr>
                  <td style="padding: 12px 0; border-bottom: 1px solid #FFE8CC; font-size: 14px; color: #636e72; width: 40%;">플랜명</td>
                  <td style="padding: 12px 0; border-bottom: 1px solid #FFE8CC; font-size: 14px; color: #2d3436; font-weight: 600; text-align: right;">${safePlanName}</td>
                </tr>
                <!-- 만료 예정일 -->
                <tr>
                  <td style="padding: 12px 0; border-bottom: 1px solid #FFE8CC; font-size: 14px; color: #636e72;">만료 예정일</td>
                  <td style="padding: 12px 0; border-bottom: 1px solid #FFE8CC; font-size: 14px; font-weight: bold; text-align: right;">
                    <span style="color: #e67e22;">${safeExpiryDate}</span>
                    <span style="display: inline-block; background-color: #e67e22; color: #ffffff; font-size: 10px; padding: 2px 6px; border-radius: 4px; margin-left: 6px; vertical-align: middle;">만료 예정</span>
                  </td>
                </tr>
                <!-- 결제 금액 -->
                <tr>
                  <td style="padding: 16px 0 0 0; font-size: 15px; color: #2d3436; font-weight: bold;">갱신 금액</td>
                  <td style="padding: 16px 0 0 0; text-align: right;">
                    <span style="font-size: 20px; color: #7a4cd1; font-weight: bold;">${safePaymentAmount}원</span>
                    <span style="font-size: 12px; color: #636e72; margin-left: 4px;">(VAT 별도)</span>
                  </td>
                </tr>
              </table>
            </div>
          </td>
        </tr>
        <!-- CTA 버튼 -->
        <tr>
          <td style="padding: 0 40px 16px 40px; text-align: center;">
            <table border="0" cellspacing="0" cellpadding="0" style="margin: 0 auto;">
              <tr>
                <td align="center" bgcolor="#00A3A0" style="border-radius: 6px;">
                  <a href="https://report.qesg.co.kr/" target="_blank" style="display: inline-block; padding: 16px 40px; font-size: 16px; color: #ffffff; text-decoration: none; font-weight: 600;" rel="noopener">
                    지금 바로 갱신하기
                  </a>
                </td>
              </tr>
            </table>
          </td>
        </tr>
        <!-- 결제 내역 관리 안내 -->
        <tr>
          <td style="padding: 0 40px 40px 40px; text-align: center;">
            <p style="margin: 0; font-size: 13px; color: #636e72;">
              결제 내역은 ERT 내 <a href="https://report.qesg.co.kr/" target="_blank" style="color: #7a4cd1; font-weight: 600; text-decoration: none;" rel="noopener">결제 내역 관리</a> 에서 확인하실 수 있습니다.
            </p>
          </td>
        </tr>
        <!-- 푸터 -->
        <tr>
          <td style="padding: 0 40px 40px 40px;">
            <p style="margin: 0; font-size: 13px; color: #b2bec3; line-height: 1.5;">
              본 메일은 지정된 수신자만 이용 가능합니다. <br/>만약 본인이 아니라면 info@qesg.co.kr로 문의해 주시기 바랍니다.<br /><br />
              &copy; 2026 Quantified ESG. All rights reserved.
            </p>
          </td>
        </tr>
      </table>
      <!-- 하단 링크 -->
      <table border="0" cellspacing="0" cellpadding="0" style="width: 100%; max-width: 600px; margin-top: 20px;">
        <tr>
          <td align="center">
            <p style="font-size: 12px; color: #b2bec3;">
              <a href="https://report.qesg.co.kr/" style="color: #b2bec3; text-decoration: underline;">ERT 웹사이트 방문</a> |
              <span style="color: #b2bec3;">문의. info@qesg.co.kr</span>
            </p>
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>`;

  return {
    mail_title,
    mail_content: wrapWithPlainTextToggle(mail_content),
    mail_type: MAIL_TYPES.DEADLINE_APPROACHING,
    recipients: [{ email: recipientEmail, variables: { name: userName } }]
  };
}

/**
 * 메일 발송
 * @param {{ recipients, mail_title, mail_content, mail_type }} payload
 */
export function sendMail({ recipients, mail_title, mail_content, mail_type }) {
  return v3Api.post('/mails', { recipients, mail_title, mail_content, mail_type });
}
